import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { Paper, Typography, Tooltip, IconButton, Fab } from '@material-ui/core';
import { BackButton, localization, getDimensionsOfPagination } from '../../sc-utilities';
import MaterialTable from 'material-table';
import * as actions from './store/logoActions';
import * as action from '../../np-core/Auth/store/authActions';
import { hasPermission, hasAtLeastOnePermission } from '../../sc-utilities';
import { Refresh, Add, Delete } from '@material-ui/icons';
import LogoDelete from './LogoDelete';
import Toolbar from '../../sc-utilities/Toolbar';
import { MuiTableRows } from '../../../themes';

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const LogoList = props => {
    const { classes, onBulkDeleteLogos, deleting, translations, onFetchLogos, onFetchLogosCount, fetching, saveColumnsFilter, activeCustomer, tableColumns, permissions, redirectHandler, tableRef } = props;
    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []); const [deleteModalState, setDeleteModalState] = useState({ open: false, logoID: null, logoIDs: [] });

    const selectionPermissions = hasAtLeastOnePermission(permissions, ['logo_delete']);

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: selectionPermissions,
        selectionProps: rowData => {
            return { id: `checkbox_${rowData.LogoID}` }
        },
        search: false,
        minBodyHeight: window.innerHeight - 290 < 300 ? 300 : window.innerHeight - 290,
        maxBodyHeight: window.innerHeight - 290,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    }
    async function fetchTableData(query) {
        let q = filterQuery(query.filters);

        query['ActiveCustomerID'] = activeCustomer;
        query['q'] = q;
        let count = await onFetchLogosCount(query);
        if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
            let dimensions = getDimensionsOfPagination(count)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }
        let result = count ? await onFetchLogos(query) : [];
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result, page: query.page, pageSize: pageSize, totalCount: count };
    }

    const handleDelete = async event => {
        if (!event) {
            setDeleteModalState({ open: false, logoID: null, logoIDs: [] });
            return;
        }
        const logoID = event.currentTarget.value;
        setDeleteModalState({ ...deleteModalState, open: true, logoID: logoID });
    };

    const handleBulkDelete = logoIDs => {
        setDeleteModalState({ ...deleteModalState, open: true, logoIDs: logoIDs });
    }

    const handleCloseSafe = () => {
        handleDelete();
    }

    const handleCloseDanger = async () => {
        await onBulkDeleteLogos({ LogoIDs: deleteModalState.logoIDs, ActiveCustomerID: activeCustomer });
        handleDelete();
        tableRef.current && tableRef.current.onQueryChange()
    }

    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} logos`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(id => id.LogoID))
        }
    ];

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography className={classes.typography}>{translations["logos"] || 'Logos'}</Typography>
                {hasPermission(permissions, 'logo_create') ? <Fab
                    id="create_logo"
                    variant="extended"
                    size="small"
                    color="primary"
                    aria-label="Add"
                    className={classes.fab}
                    style={{ borderRadius: '1px' }}
                    onClick={() => redirectHandler(`/settings/logo/create`)}
                >
                    <Add size="small" />
                    {translations["create_new"] || "Create new"}
                </Fab> : null}
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" disabled={fetching} onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>
        </div>
    }


    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })
        objToSendColumn[`Logo`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomer })
        return newColumnState
    }

    return (
        <Paper>
            <MaterialTable
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                isLoading={fetching}
                deleting={deleting}
                components={{
                    Toolbar: props => (
                        <Toolbar data={props} actions={tableActions} selectedRows={props.selectedRows} translations={translations} />
                    )
                }}
                localization={localization(translations)}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
            {hasPermission(permissions, 'logo_delete') ? <LogoDelete handleCloseSafe={handleCloseSafe} handleCloseDanger={handleCloseDanger} translations={translations} entityName={'Logo'} deleteModalState={deleteModalState} /> : null}

        </Paper>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomer: state?.core?.login?.activeCustomer?.CustomerID ?? null,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        fetching: state.setting.logos.fetching,
        deleting: state.setting.logos.deleting,
        permissions: state?.core?.login?.permissions || []

    };
};

const mapDispatchToProps = dispatch => {
    return {
        onBulkDeleteLogos: logo => dispatch(actions.bulkDeleteLogos(logo)),
        onFetchLogos: filter => dispatch(actions.fetchLogo(filter)),
        onFetchLogosCount: filter => dispatch(actions.fetchLogoCount(filter)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(LogoList));